"use client";
import { usePathname } from "next/navigation";

import { FooterReallySimple } from "@/components/common/FooterReallySimple";
import { FooterSimple } from "@/components/common/FooterSimple";
import { FOOTER_TYPES } from "@/constants/footer";
import { FactoryProps } from "@/types/common";
import { getLayoutDataBySlug } from "@/utils/getLayoutDataBySlug";
import { getStickyBottomBarClearanceClass } from "@/utils/stickyBottomBar";
import { currentTheme } from "@/utils/theming";

import { Footer } from "./Footer";

export default function FooterFactory({ cmsData }: FactoryProps) {
  const { allContentfulLayoutData } = cmsData;
  const pathname = usePathname();
  const layoutDataBySlug = getLayoutDataBySlug(pathname, allContentfulLayoutData);
  const footerType = layoutDataBySlug?.footer_type || FOOTER_TYPES.DEFAULT;
  const theme = currentTheme(pathname);

  if (theme === "app" || footerType === FOOTER_TYPES.NONE) {
    return null;
  }

  if (footerType === FOOTER_TYPES.SIMPLE) {
    return <FooterSimple theme={theme as "men" | "master"} />;
  }

  /*
   * The quiz results page, checkout, and cart each have a sticky mobile/tablet
   * action bar ("Continue" / "Place Order" / "Proceed to Checkout") that would
   * otherwise cover the tail of the footer, so each needs extra bottom
   * clearance below it. In webview/app theme this component isn't rendered at
   * all (see the early return above), so MainFactory supplies the same
   * clearance directly on `<main>` in that case instead — see
   * src/utils/stickyBottomBar.ts.
   */
  const stickyBarClearance = getStickyBottomBarClearanceClass(pathname);

  if (footerType === FOOTER_TYPES.REALLYSIMPLE) {
    return (
      <div className={stickyBarClearance}>
        <FooterReallySimple />
      </div>
    );
  }

  return (
    <div className={stickyBarClearance}>
      <Footer theme={theme} />
    </div>
  );
}
